'use client';

import {
	CABINA_PRIMARIA_PLACEHOLDER,
	DSO_SELECT_OPTIONS,
	isValidCabinaPrimaria,
} from '@slims/shared/constants';
import type { MemberAppliance } from '@slims/shared/energy';
import {
	type ApplianceDraft,
	appliancesToDraft,
	draftToAppliances,
	MemberMeterView,
	type ConsumptionQ,
	EMPTY_CONSUMPTION_Q,
} from '@slims/ui/organisms';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/utils/api';
import { useAdminScopeStore } from '@/stores/admin-scope-store';
import { BollettaUploadSection } from '@/components/members/bolletta-upload-section';
import { MeterReaderSection } from '@/components/members/meter-reader-section';
import { TelemetryPanel } from '@/components/members/telemetry-panel';
import { TypicalProfileSection } from '@/components/members/typical-profile-section';
import { meterDraftFields } from '@/components/members/meter-draft-fields';
import { useQueryClient } from '@tanstack/react-query';
import { useMeterDraft } from '@/components/members/use-meter-draft';
import type { Meter } from '@/hooks/use-meters';

/**
 * Vero se il questionario contiene almeno una risposta.
 *
 * Serve al solo caso in cui il consumo annuo sia stato dichiarato senza scegliere
 * una categoria d'utenza: senza questo controllo la PATCH manderebbe `null` —
 * perché la categoria è vuota — e il numero appena digitato andrebbe perso.
 */
function hasAnyAnswer(q: ConsumptionQ): boolean {
	return Object.values(q).some((v) => String(v ?? '').trim() !== '');
}

interface ConnessioneConsumoTabProps {
	meter?: Meter;
	onUpdate: (id: string, patch: Record<string, unknown>) => Promise<unknown>;
	onOpenGateway: (meter: Meter) => void;
	/** Dati del socio da cui il profilo tipico è dedotto. */
	ruolo?: string | null;
	tipo?: string | null;
	hasStorage?: boolean | null;
	/** Il socio a cui appartiene il punto: il questionario consumi è suo, non del misuratore. */
	memberId: string;
	/** Categoria BDEW/SLP salvata, da `members.consumption_profile`. */
	consumptionProfile: string;
	/** Risposte al questionario, da `members.consumption_profile_data`. */
	consumptionData: Partial<ConsumptionQ> | null;
	/** I dispositivi elettrici del socio, da `members.dispositivi`. */
	dispositivi?: MemberAppliance[];
	/** Ricarica i punti di misura: il profilo tipico cambia con il consumo dichiarato. */
	onRefetchMeters: () => Promise<unknown>;
	/** Registra il salvataggio combinato (misuratore + questionario) di questo tab. */
	onRegisterSave?: (save: () => Promise<boolean>) => void;
	/** Misuratore o questionario con modifiche non salvate. */
	onDirtyChange?: (dirty: boolean) => void;
}

/** Profilo del prelievo, bolletta, curva e collegamento del lettore. */
export function ConnessioneConsumoTab({
	meter,
	onUpdate,
	onOpenGateway,
	ruolo,
	tipo,
	hasStorage,
	memberId,
	consumptionProfile,
	consumptionData,
	dispositivi,
	onRefetchMeters,
	onRegisterSave,
	onDirtyChange,
}: ConnessioneConsumoTabProps) {
	const d = useMeterDraft(meter, onUpdate);
	const orgId = useAdminScopeStore((s) => s.configId);
	const initialConsumption = useRef({ profile: consumptionProfile, q: consumptionData ?? {} });
	const [profile, setProfile] = useState(consumptionProfile);
	const [q, setQ] = useState<ConsumptionQ>({
		...EMPTY_CONSUMPTION_Q,
		...(consumptionData ?? {}),
	});
	const queryClient = useQueryClient();

	// I dispositivi hanno la loro PATCH: stanno sul socio, e non toccano le curve.
	const savedAppliances = JSON.stringify(draftToAppliances(appliancesToDraft(dispositivi)));
	const [appliances, setAppliances] = useState<ApplianceDraft[]>(() => appliancesToDraft(dispositivi));
	const appliancesDirty = JSON.stringify(draftToAppliances(appliances)) !== savedAppliances;
	// Il socio arriva dopo il primo render: finché non si tocca nulla, l'elenco lo segue.
	const appliancesTouched = useRef(false);
	useEffect(() => {
		if (!appliancesTouched.current) setAppliances(appliancesToDraft(dispositivi));
	}, [savedAppliances]);

	async function persistAppliances(): Promise<boolean> {
		try {
			const res = await apiFetch(`/api/members/${memberId}?orgId=${orgId}`, {
				method: 'PATCH',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ dispositivi: draftToAppliances(appliances) }),
			});
			if (!res.ok) {
				const err = (await res.json().catch(() => null)) as { error?: string } | null;
				toast.error(err?.error ?? 'Errore nel salvataggio dei dispositivi');
				return false;
			}
			appliancesTouched.current = false;
			return true;
		} catch {
			toast.error('Errore di rete');
			return false;
		}
	}

	// Il questionario ha un salvataggio suo (`persistConsumption`, indipendente
	// dalla bozza del misuratore): `saveAll` sotto deve sapere se è stato
	// toccato per includerlo nel "Salva" della pagina.
	const questionnaireDirty =
		profile !== initialConsumption.current.profile ||
		JSON.stringify(q) !==
			JSON.stringify({ ...EMPTY_CONSUMPTION_Q, ...initialConsumption.current.q });

	/**
	 * Scrive il questionario sul socio.
	 *
	 * Consumo annuo e caratteristiche finiscono nella stessa colonna JSON, quindi
	 * passano dalla stessa PATCH nel salvataggio unico del profilo. `consumptionProfileData` è fra i `TYPICAL_FIELDS` dell'API: la
	 * risposta arriva col profilo tipico già riscritto, ed è per questo che si
	 * ricaricano i punti di misura — senza, la card GSE resterebbe a mostrare il
	 * consumo di prima.
	 */
	async function persistConsumption(next: ConsumptionQ): Promise<boolean> {
		try {
			const res = await apiFetch(`/api/members/${memberId}?orgId=${orgId}`, {
				method: 'PATCH',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					consumptionProfile: profile || null,
					consumptionProfileData: profile || hasAnyAnswer(next) ? next : null,
				}),
			});
			if (!res.ok) {
				toast.error('Errore nel salvataggio');
				return false;
			}
			await onRefetchMeters();
			void queryClient.invalidateQueries({ queryKey: ['meter-hourly', orgId, meter?.id] });
			initialConsumption.current = { profile, q: next };
			return true;
		} catch {
			toast.error('Errore di rete');
			return false;
		}
	}

	async function saveAll(): Promise<boolean> {
		const raw = q.consumoAnnuoKwh.trim();
		if (questionnaireDirty && raw !== '' && !(Number(raw) > 0)) {
			toast.error('Il consumo annuo deve essere un numero maggiore di zero.');
			return false;
		}
		if (!(await d.save())) return false;
		if (appliancesDirty && !(await persistAppliances())) return false;
		if (questionnaireDirty) return await persistConsumption(q);
		return true;
	}
	const onRegisterSaveRef = useRef(onRegisterSave);
	onRegisterSaveRef.current = onRegisterSave;
	useEffect(() => {
		onRegisterSaveRef.current?.(saveAll);
	});
	// Vedi la stessa nota in useMeterDraft: un tab smontato non deve lasciare
	// alla pagina un salvataggio agganciato a una bozza congelata.
	useEffect(() => () => onRegisterSaveRef.current?.(async () => true), []);

	const tabDirty = d.dirty || questionnaireDirty || appliancesDirty;
	const onDirtyChangeRef = useRef(onDirtyChange);
	onDirtyChangeRef.current = onDirtyChange;
	useEffect(() => {
		onDirtyChangeRef.current?.(tabDirty);
	}, [tabDirty]);
	useEffect(() => () => onDirtyChangeRef.current?.(false), []);

	return (
		<MemberMeterView
			kind="M1"
			fields={meter ? meterDraftFields(d) : undefined}
			onFieldChange={d.set}
			cabinaPlaceholder={CABINA_PRIMARIA_PLACEHOLDER}
			cabinaError={
				isValidCabinaPrimaria(d.str('cabinaPrimaria'))
					? undefined
					: `Formato non valido: usa un codice come ${CABINA_PRIMARIA_PLACEHOLDER}`
			}
			dsoOptions={DSO_SELECT_OPTIONS}
			declaredAnnualKwh={q.consumoAnnuoKwh}
			onDeclaredAnnualKwhChange={(v) => setQ({ ...q, consumoAnnuoKwh: v })}
			consumption={{ profile, onProfileChange: setProfile, q, onQChange: setQ }}
			appliances={{
				rows: appliances,
				onChange: (rows) => {
					appliancesTouched.current = true;
					setAppliances(rows);
				},
			}}
			typicalProfile={
				meter && (
					<TypicalProfileSection
						meter={meter}
						ruolo={ruolo}
						tipo={tipo}
						hasStorage={hasStorage}
						onUpdate={onUpdate}
					/>
				)
			}
			bill={meter && <BollettaUploadSection meter={meter} />}
			curve={
				meter && (
					<TelemetryPanel
						meterId={meter.id}
						podId={meter.podId}
						series="prelevata"
						seriesLabel="Consumo"
						accent="#7c3aed"
					/>
				)
			}
			reader={
				meter && (
					<MeterReaderSection meter={meter} onUpdate={onUpdate} onOpenGateway={onOpenGateway} />
				)
			}
		/>
	);
}
